import React, { useContext, useState } from "react";
import { ArrowLeftOutlined, ArrowRightOutlined } from "@ant-design/icons";
import message from "antd/lib/message";
import Radio from "antd/lib/radio";
import Form from "antd/lib/form";
import { useMutation } from "react-query";
import { createApiClient } from "apiClient";
import { AppButton } from "components/button";
import { ButtonType } from "components/button/appButton";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { UserContext, UserContextState } from "contextApis/userContext";
import { Platform, UserRoles } from "constants/roles";
import { FormSubmitButton } from "pages/symptoms/surveyForms/FormSubmitButton";
import { FormClearButton } from "pages/symptoms/surveyForms/FormClearButton";
import {
  SurveyPage,
  SurveyContent,
  SurveyHeader,
  SurveyNoticeCard,
  SurveyQuestionCard,
  AnswerList,
  AnswerOption,
  SurveyFooter,
  SurveyProgressBar,
  FooterActions,
  FormItemStyle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface WeightChangeFormProps {
  setShowSurveyForm(show: boolean): void;
  selectedItems: string[];
  goToNextFormOrReturn(): void;
}

export const WeightChangeForm: React.FC<WeightChangeFormProps> = ({
  setShowSurveyForm,
  selectedItems,
  goToNextFormOrReturn
}) => {
  const params = new URLSearchParams(window.location.search);
  const platform = params.get("platform");
  const [currentPage, setCurrentPage] = useState(1);

  const [form] = Form.useForm();

  const userContext = useContext<UserContextState>(UserContext);
  const patientContext = useContext<PatientContextState>(PatientContext);

  const [showSubmit, setShowSubmit] = useState(false);

  const userRole = sessionStorage.getItem("userRole");

  const {
    isLoading: isLoadingWeightChangeMutation,
    mutate: submitWeightChangeForm
  } = useMutation<string>(
    "add-bill",
    async () => {
      return await createApiClient().addWeightChangeFormData({
        patient_internal_id:
          userRole === UserRoles.PATIENT && userContext.userData
            ? userContext.userData?.internal_id
            : patientContext?.patientData?.id || "",
        report: localStorage.getItem("reportChangeInWeight") || "",
        period: localStorage.getItem("weightChangeOverPeriod") || "",
        gained_lost: localStorage.getItem("gainedOrLostWeight") || "",
        lb_kg: localStorage.getItem("poundsOrKilograms") || "",
        change_in_lb: localStorage.getItem("weightChangeInPounds") || "",
        change_in_kg: localStorage.getItem("weightChangeInKilograms") || "",
        submitted_by: `${userContext.userData?.firstName} ${userContext.userData?.lastName}`
      });
    },
    {
      onSuccess: () => {
        message.success("Form Submitted Successfully");
        goToNextFormOrReturn();
      },
      onError: () => {
        message.error({
          content: (
            <span>
              There was a problem submitting this form, resubmit or{" "}
              <a onClick={() => goToNextFormOrReturn()}>
                click here to continue to the next survey
              </a>
            </span>
          ),
          style: {
            bottom: "10px",
            position: "fixed",
            left: "30%"
          },
          duration: 5
        });
      }
    }
  );

  const onFinish = () => {
    if (localStorage.getItem("reportChangeInWeight") === "No") {
      form.resetFields([
        "Change over period",
        "Gained or lost weight",
        "Pounds or kilograms",
        "Weight change in pounds",
        "Weight change in kilograms"
      ]);
      localStorage.setItem("weightChangeOverPeriod", "");
      localStorage.setItem("gainedOrLostWeight", "");
      localStorage.setItem("poundsOrKilograms", "");
      localStorage.setItem("weightChangeInPounds", "");
      localStorage.setItem("weightChangeInKilograms", "");
      submitWeightChangeForm();
    } else if (currentPage === 1) {
      setCurrentPage(2);
    } else if (currentPage === 2) {
      if (localStorage.getItem("poundsOrKilograms") === "Pounds (lb)") {
        localStorage.setItem("weightChangeInKilograms", "");
      } else {
        localStorage.setItem("weightChangeInPounds", "");
      }
      setCurrentPage(3);
    } else {
      submitWeightChangeForm();
    }
  };

  const onClear = () => {
    if (currentPage === 1) {
      form.resetFields(["Do you want to report weight change"]);
    } else if (currentPage === 2) {
      form.resetFields([
        "Change over period",
        "Gained or lost weight",
        "Pounds or kilograms"
      ]);
    } else {
      form.resetFields([
        "Weight change in pounds",
        "Weight change in kilograms"
      ]);
    }
  };

  const currentReportOption = localStorage.getItem("reportChangeInWeight");
  const totalPages = currentReportOption === "No" ? 1 : 3;
  const progressPercent = Math.round((currentPage / totalPages) * 100);

  return (
    <SurveyPage platform={platform}>
      <div
        style={{
          marginBottom: "15px",
          display: platform === Platform.MOBILE ? "none" : "block"
        }}
      >
        <AppButton
          type={ButtonType.Primary}
          onClick={() => setShowSurveyForm(false)}
          buttonContent={
            <>
              <ArrowLeftOutlined /> Back
            </>
          }
        />
      </div>

      <SurveyContent>
        <Form
          name="weightChange"
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          style={{ width: "100%" }}
        >
          <SurveyHeader title={`Survey - ${selectedItems[0]}`} />

          <SurveyProgressBar
            percent={progressPercent}
            currentStep={currentPage}
            totalSteps={totalPages}
          />

          {currentPage === 1 && (
            <>
              <SurveyNoticeCard />

              <SurveyQuestionCard
                title="Do you want to report a change in your weight?"
                required
              >
                <FormItemStyle
                  name="Do you want to report weight change"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("reportChangeInWeight")}
                    onChange={e => {
                      if (e.target.value === "No") {
                        setShowSubmit(true);
                      } else {
                        setShowSubmit(false);
                      }
                      localStorage.setItem(
                        "reportChangeInWeight",
                        e.target.value
                      );
                    }}
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Yes">Yes</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="No">No</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 2 && (
            <>
              <SurveyQuestionCard
                title="Over what period of time has your weight changed?"
                required
              >
                <FormItemStyle
                  name="Change over period"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("weightChangeOverPeriod")}
                    onChange={e =>
                      localStorage.setItem(
                        "weightChangeOverPeriod",
                        e.target.value
                      )
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="1 - 2 Weeks">1 - 2 Weeks</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="2 - 4 Weeks">2 - 4 Weeks</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="1 - 3 Months">1 - 3 Months</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="4 - 6 Months">4 - 6 Months</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="Have you gained or lost weight?"
                required
              >
                <FormItemStyle
                  name="Gained or lost weight"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("gainedOrLostWeight")}
                    onChange={e =>
                      localStorage.setItem("gainedOrLostWeight", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Gained">Gained</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Lost">Lost</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>

              <SurveyQuestionCard
                title="Please indicate if you are reporting your weight change in Pounds or Kilograms"
                required
              >
                <FormItemStyle
                  name="Pounds or kilograms"
                  label=""
                  rules={[{ required: true }]}
                  initialValue={""}
                >
                  <Radio.Group
                    value={localStorage.getItem("poundsOrKilograms")}
                    onChange={e =>
                      localStorage.setItem("poundsOrKilograms", e.target.value)
                    }
                    style={{ width: "100%" }}
                  >
                    <AnswerList>
                      <AnswerOption>
                        <Radio value="Pounds (lb)">Pounds (lb)</Radio>
                      </AnswerOption>
                      <AnswerOption>
                        <Radio value="Kilograms (Kg)">Kilograms (Kg)</Radio>
                      </AnswerOption>
                    </AnswerList>
                  </Radio.Group>
                </FormItemStyle>
              </SurveyQuestionCard>
            </>
          )}

          {currentPage === 3 && (
            <>
              {localStorage.getItem("poundsOrKilograms") === "Pounds (lb)" ? (
                <SurveyQuestionCard
                  title="How much weight (in pounds) have you gained or lost?"
                  required
                >
                  <FormItemStyle
                    name="Weight change in pounds"
                    label=""
                    rules={[{ required: true }]}
                    initialValue={""}
                  >
                    <Radio.Group
                      value={localStorage.getItem("weightChangeInPounds")}
                      onChange={e =>
                        localStorage.setItem(
                          "weightChangeInPounds",
                          e.target.value
                        )
                      }
                      style={{ width: "100%" }}
                    >
                      <AnswerList>
                        <AnswerOption>
                          <Radio value="1 - 3 pounds">1 - 3 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="3 - 6 pounds">3 - 6 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="7 - 10 pounds">7 - 10 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="11 - 15 pounds">11 - 15 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="16 - 20 pounds">16 - 20 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="20 - 30 pounds">20 - 30 pounds</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="More than 30 pounds">
                            More than 30 pounds
                          </Radio>
                        </AnswerOption>
                      </AnswerList>
                    </Radio.Group>
                  </FormItemStyle>
                </SurveyQuestionCard>
              ) : (
                <SurveyQuestionCard
                  title="How much weight (in kg) have you gained or lost?"
                  required
                >
                  <FormItemStyle
                    name="Weight change in kilograms"
                    label=""
                    rules={[{ required: true }]}
                    initialValue={""}
                  >
                    <Radio.Group
                      value={localStorage.getItem("weightChangeInKilograms")}
                      onChange={e =>
                        localStorage.setItem(
                          "weightChangeInKilograms",
                          e.target.value
                        )
                      }
                      style={{ width: "100%" }}
                    >
                      <AnswerList>
                        <AnswerOption>
                          <Radio value="1 kg or less">1 kg or less</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="2 - 3 kg">2 - 3 kg</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="4 - 5 kg">4 - 5 kg</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="6 - 7 kg">6 - 7 kg</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="8 - 10 kg">8 - 10 kg</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="11 - 15 kg">11 - 15 kg</Radio>
                        </AnswerOption>
                        <AnswerOption>
                          <Radio value="More than 15 kg">More than 15 kg</Radio>
                        </AnswerOption>
                      </AnswerList>
                    </Radio.Group>
                  </FormItemStyle>
                </SurveyQuestionCard>
              )}
            </>
          )}

          <SurveyFooter>
            <SurveyProgressBar currentPage={currentPage} totalPages={3} />

            <FooterActions>
              <Form.Item>
                <FormClearButton onClear={onClear} />
              </Form.Item>

              {currentPage !== 1 && (
                <AppButton
                  type={ButtonType.Primary}
                  buttonContent={
                    <>
                      <ArrowLeftOutlined /> Back
                    </>
                  }
                  onClick={() =>
                    setCurrentPage(currentPage !== 1 ? currentPage - 1 : 1)
                  }
                  style={{ width: "105px" }}
                />
              )}

              <Form.Item>
                <FormSubmitButton
                  type={ButtonType.Primary}
                  buttonContent={
                    currentPage === 3 || showSubmit ? (
                      "Submit"
                    ) : (
                      <>
                        Next <ArrowRightOutlined />
                      </>
                    )
                  }
                  htmlType="submit"
                  loading={isLoadingWeightChangeMutation}
                />
              </Form.Item>
            </FooterActions>
          </SurveyFooter>
        </Form>
      </SurveyContent>
    </SurveyPage>
  );
};
